import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/UI/Button';
import { ConfirmModal } from '../../components/UI/ConfirmModal';
import { useToast } from '../../components/UI/ToastProvider';

interface ApiCredential {
  id: string;
  clientId: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  customerId: string;
  customerName: string;
}

interface CreateApiCredentialResult extends ApiCredential {
  clientSecret: string;
}

interface CustomerApiCredentialsPanelProps {
  customerId: string;
}

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { message?: string; error?: string; title?: string } } })
    ?.response?.data;
  if (response?.error)
  {
    return response.error;
  }
  if (response?.message)
  {
    return response.message;
  }
  if (response?.title)
  {
    return response.title;
  }
  return fallback;
};

const formatDateTime = (value?: string | null): string => {
  if (!value)
  {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime()))
  {
    return value;
  }

  return date.toLocaleString('pt-BR');
};

export const CustomerApiCredentialsPanel: React.FC<CustomerApiCredentialsPanelProps> = ({ customerId }) => {
  const { addToast } = useToast();
  const [credentials, setCredentials] = useState<ApiCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState<{ open: boolean; credential: ApiCredential | null }>({
    open: false,
    credential: null,
  });
  const [revealedSecret, setRevealedSecret] = useState<{ id: string; clientId: string; secret: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const loadCredentials = useCallback(async () => {
    setLoading(true);
    try
    {
      const response = await api.get<ApiCredential[]>(`/api-credentials/by-customer/${customerId}`);
      setCredentials(response.data);
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao carregar credenciais de API.'), 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [customerId, addToast]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim())
    {
      return;
    }

    setCreating(true);
    try
    {
      const response = await api.post<CreateApiCredentialResult>('/api-credentials', {
        customerId,
        name: newName.trim(),
      });
      setCredentials((prev) => [response.data, ...prev]);
      setRevealedSecret({
        id: response.data.id,
        clientId: response.data.clientId,
        secret: response.data.clientSecret,
      });
      setNewName('');
      setShowForm(false);
      addToast('Credencial criada com sucesso. Copie o secret antes de fechar.', 'success');
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao criar credencial de API.'), 'error');
    }
    finally
    {
      setCreating(false);
    }
  };

  const handleToggleActive = async (credential: ApiCredential) => {
    try
    {
      const response = await api.put<ApiCredential>(`/api-credentials/${credential.id}`, {
        name: credential.name,
        isActive: !credential.isActive,
      });
      setCredentials((prev) => prev.map((c) => (c.id === credential.id ? response.data : c)));
      addToast(
        response.data.isActive ? 'Credencial ativada.' : 'Credencial desativada.',
        'success',
      );
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao atualizar credencial.'), 'error');
    }
  };

  const handleRegenerate = async () => {
    if (!confirmRegenerate.credential)
    {
      return;
    }

    try
    {
      const response = await api.post<CreateApiCredentialResult>(
        `/api-credentials/${confirmRegenerate.credential.id}/regenerate-secret`,
      );
      setRevealedSecret({
        id: response.data.id,
        clientId: response.data.clientId,
        secret: response.data.clientSecret,
      });
      addToast('Secret regenerado com sucesso. Copie o novo secret antes de fechar.', 'success');
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao regenerar secret.'), 'error');
    }
    finally
    {
      setConfirmRegenerate({ open: false, credential: null });
    }
  };

  const handleCopy = async (text: string) => {
    try
    {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    catch
    {
      addToast('Não foi possível copiar automaticamente. Copie manualmente.', 'error');
    }
  };

  if (loading)
  {
    return <div className="py-8 text-center text-gray-500">Carregando...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Credenciais de API</h2>
        {!showForm && (
          <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
            Nova Credencial
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-gray-50 border rounded p-4 mb-4">
          <h3 className="font-medium mb-3">Nova Credencial</h3>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Ex.: Integração ERP, Portal Externo..."
                maxLength={200}
                required
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm" disabled={creating || !newName.trim()}>
                {creating ? 'Criando...' : 'Criar'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowForm(false);
                  setNewName('');
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </form>
      )}

      {revealedSecret && (
        <div className="border border-yellow-300 bg-yellow-50 rounded p-4 mb-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-sm font-semibold text-yellow-800">
              Guarde o secret agora — ele não será exibido novamente.
            </p>
            <button
              type="button"
              className="text-yellow-600 hover:text-yellow-800 text-lg leading-none"
              onClick={() => setRevealedSecret(null)}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600 font-medium">Client ID: </span>
              <code className="bg-white border rounded px-2 py-0.5 text-xs select-all">
                {revealedSecret.clientId}
              </code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 font-medium">Client Secret: </span>
              <code className="bg-white border rounded px-2 py-0.5 text-xs select-all flex-1 break-all">
                {revealedSecret.secret}
              </code>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => handleCopy(revealedSecret.secret)}
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {credentials.length === 0 && !showForm && (
        <p className="text-gray-500 text-sm py-4 text-center">Nenhuma credencial cadastrada.</p>
      )}

      {credentials.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="px-3 py-2 font-medium text-gray-700">Nome</th>
                <th className="px-3 py-2 font-medium text-gray-700">Client ID</th>
                <th className="px-3 py-2 font-medium text-gray-700">Status</th>
                <th className="px-3 py-2 font-medium text-gray-700">Criado em</th>
                <th className="px-3 py-2 font-medium text-gray-700 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {credentials.map((credential) => (
                <tr key={credential.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{credential.name}</td>
                  <td className="px-3 py-2">
                    <code className="text-xs bg-gray-100 rounded px-1 py-0.5">{credential.clientId}</code>
                  </td>
                  <td className="px-3 py-2">
                    {credential.isActive ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        Inativo
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{formatDateTime(credential.createdAt)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        title={credential.isActive ? 'Desativar' : 'Ativar'}
                        onClick={() => handleToggleActive(credential)}
                      >
                        {credential.isActive ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        title="Regenerar secret"
                        onClick={() => setConfirmRegenerate({ open: true, credential })}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmRegenerate.open}
        title="Regenerar secret"
        message={`Tem certeza que deseja regenerar o secret da credencial "${confirmRegenerate.credential?.name}"? O secret atual deixará de funcionar imediatamente.`}
        confirmText="Regenerar"
        onConfirm={handleRegenerate}
        onCancel={() => setConfirmRegenerate({ open: false, credential: null })}
      />
    </div>
  );
};
